import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatTimeHHmm } from '../services/lstMatrixHourly';
import {
  buildPlannerBlocks,
  mergePlannerBlocks,
  snapHour,
  type PlannerBlock,
} from '../services/tplPlannerBlocks';
import { logPlannerMatrixConsistency } from '../services/tplPlannerMatrixDebug';
import type { LstCargo, LstTemplate } from '../types';
import { filterTemplatesForUser } from '../utils/lstTemplateScope';

export interface UseTplPlannerSyncOptions {
  templates: LstTemplate[];
  cargo: LstCargo;
  userId: string;
  /** Datas da semana corrente — alinha recorrência mensal com a Matriz. */
  weekDates?: string[];
  /** Emite log de debug Matriz × Planner (temporário). */
  debugConsistency?: boolean;
  onPersistTime?: (templateId: string, scheduledTime: string) => void;
}

/**
 * Single Source of Truth — blocos do Planner Semanal compartilhados entre
 * Construtor TPL e listagem diária do Meu Padrão (Heijunka).
 * Mesmo conjunto de rotinas que a Matriz Kamishibai (templateAppliesToUser + isTemplateDueOnDate).
 */
export function useTplPlannerSync({
  templates,
  cargo,
  userId,
  weekDates = [],
  debugConsistency = false,
  onPersistTime,
}: UseTplPlannerSyncOptions) {
  const scopedTemplates = useMemo(
    () => filterTemplatesForUser(templates, cargo, userId),
    [templates, cargo, userId],
  );

  const baseBlocks = useMemo(
    () => buildPlannerBlocks(scopedTemplates, weekDates),
    [scopedTemplates, weekDates],
  );

  const [overrides, setOverrides] = useState<Record<string, { day: number; hour: string }>>({});

  const blocks = useMemo(
    () => mergePlannerBlocks(baseBlocks, overrides),
    [baseBlocks, overrides],
  );

  useEffect(() => {
    if (!debugConsistency || weekDates.length === 0) return;
    logPlannerMatrixConsistency(templates, weekDates, cargo, userId, blocks);
  }, [debugConsistency, templates, weekDates, cargo, userId, blocks]);

  /** Limpa overrides quando o servidor reflete o horário persistido. */
  useEffect(() => {
    setOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const t of scopedTemplates) {
        const o = next[t.id];
        if (!o) continue;
        const serverHour = snapHour(formatTimeHHmm(t.scheduled_time) ?? o.hour);
        if (serverHour === o.hour) {
          delete next[t.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [scopedTemplates]);

  const moveBlock = useCallback(
    (templateId: string, day: number, hour: string) => {
      setOverrides((prev) => ({ ...prev, [templateId]: { day, hour } }));
      onPersistTime?.(templateId, hour);
    },
    [onPersistTime],
  );

  const resetOverrides = useCallback(() => setOverrides({}), []);

  return { blocks, moveBlock, resetOverrides, baseBlocks };
}

export type { PlannerBlock };
